/**
 * A CV as plain text for an AI prompt.
 *
 * Two experience shapes live in the database: `{ title, startDate, endDate }`
 * from the generator and `{ position, period }` from the old editor. Prompts
 * used to read one or the other and silently lost the job titles or dates of
 * the rest, so every prompt now goes through this.
 */
type Loose = Record<string, unknown>

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')
const list = (value: unknown): Loose[] => (Array.isArray(value) ? value.filter(v => v && typeof v === 'object') as Loose[] : [])
const words = (value: unknown): string[] => (Array.isArray(value) ? value.map(text).filter(Boolean) : [])

/** Long descriptions are cut so one CV cannot blow the prompt up. */
const MAX_DESCRIPTION = 800

function period(item: Loose): string {
  const given = text(item.period)
  if (given) return given
  const start = text(item.startDate)
  const end = text(item.endDate)
  if (!start && !end) return ''
  return `${start || '?'} – ${end || 'present'}`
}

export function cvPromptText(cv: Loose | null | undefined): string {
  if (!cv) return ''
  const lines: string[] = []

  if (text(cv.name)) lines.push(`Name: ${text(cv.name)}`)
  if (text(cv.title)) lines.push(`Title: ${text(cv.title)}`)
  if (text(cv.aboutMe)) lines.push(`About: ${text(cv.aboutMe)}`)

  const experiences = list(cv.experiences)
  if (experiences.length) {
    lines.push('', 'Experience:')
    for (const exp of experiences) {
      const role = text(exp.title) || text(exp.position)
      const when = period(exp)
      lines.push(`- ${[role, text(exp.company)].filter(Boolean).join(', ')}${when ? ` (${when})` : ''}`)
      const description = text(exp.description)
      if (description) lines.push(`  ${description.slice(0, MAX_DESCRIPTION)}`)
    }
  }

  const education = list(cv.education)
  if (education.length) {
    lines.push('', 'Education:')
    for (const edu of education) {
      const when = period(edu)
      lines.push(`- ${[text(edu.degree), text(edu.school)].filter(Boolean).join(', ')}${when ? ` (${when})` : ''}`)
    }
  }

  const skills = [...new Set([...words(cv.skills), ...words(cv.technicalSkills)])]
  if (skills.length) lines.push('', `Skills: ${skills.join(', ')}`)
  const soft = words(cv.softSkills)
  if (soft.length) lines.push(`Soft skills: ${soft.join(', ')}`)

  const languages = list(cv.languages).map(lang => [text(lang.name), text(lang.level)].filter(Boolean).join(' – ')).filter(Boolean)
  if (languages.length) lines.push(`Languages: ${languages.join(', ')}`)

  const certificates = list(cv.certificates).map(cert => [text(cert.name), text(cert.issuer)].filter(Boolean).join(', ')).filter(Boolean)
  if (certificates.length) lines.push(`Certificates: ${certificates.join('; ')}`)

  return lines.join('\n').trim()
}
